import Udesk from 'Udesk';
import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { Select } from 'udesk-ui';
import { putDataByIdAgent } from 'src/api/data/{id}/agent';
import { getAgentScreen } from 'src/api/agent/screen';

interface TemplateProps {
    style?: React.CSSProperties;
    height?: number;
    callId?: number;
    isEditable?: boolean;
    editableLoading?: boolean;
    fieldList?: Array<{ id?: string; label?: string; fieldName?: string; name?: string }>;
    fieldValueMap?: Record<string, string>;
}

const AGENT_FIELD_NAMES = ['ticket_assignee', 'agentNo', 'agentName', 'agent_no', 'agent_name'];

export function isEditableAgentField(field?: { id?: string; fieldName?: string; name?: string }) {
    return AGENT_FIELD_NAMES.includes(field?.name || '') && !!field?.id && !!field?.fieldName;
}

function findAgentField(
    fieldList?: Array<{ id?: string; label?: string; fieldName?: string; name?: string }>,
    fieldValueMap?: Record<string, string>
) {
    if (!fieldList || !fieldValueMap) return null;

    let agentField = fieldList.find(isEditableAgentField);

    if (!agentField) {
        const labelKeywords = ['受理客服', '客服', '工号', '处理人', '负责人', '受理人'];
        agentField = fieldList.find((f) =>
            labelKeywords.some((keyword) => f.label?.includes(keyword))
        );
    }

    if (agentField && agentField.id && agentField.fieldName) {
        const value = fieldValueMap[agentField.fieldName] || fieldValueMap[agentField.id] || '';
        return {
            id: agentField.id,
            fieldName: agentField.fieldName,
            label: agentField.label || '受理客服',
            value,
        };
    }

    return null;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { callId, isEditable, editableLoading, fieldList, fieldValueMap } = props;

    const agentField = useMemo(
        () => findAgentField(fieldList, fieldValueMap),
        [fieldList, fieldValueMap]
    );

    const [agentOptions, setAgentOptions] = useState<{ label: string; value: string }[]>([]);
    const [selectedAgentNo, setSelectedAgentNo] = useState<string | undefined>();
    const [currentOption, setCurrentOption] = useState<{ label: string; value: string }>();
    const [loading, setLoading] = useState(false);

    // 全量加载客服列表（合并，不覆盖已有的回显匹配项）
    const loadAllAgents = () => {
        if (loading) return;
        setLoading(true);
        getAgentScreen({ params: { keyWord: '' } }).then(
            (res) => {
                const newOpts =
                    res.data?.map((item) => ({
                        label: item.agentName!,
                        value: item.agentNo!,
                    })) || [];
                setAgentOptions((prev) => {
                    const existingValues = new Set(prev.map((o) => o.value));
                    const uniqueNew = newOpts.filter((o) => !existingValues.has(o.value));
                    return [...prev, ...uniqueNew];
                });
                setLoading(false);
            },
            () => setLoading(false)
        );
    };

    // 初始回显：用当前值匹配真实 agentNo，并保存为 currentOption 始终置顶
    useEffect(() => {
        if (!isEditable || !agentField?.value) {
            setSelectedAgentNo(undefined);
            setCurrentOption(undefined);
            return;
        }

        // 先用当前值兜底
        const fallback = { label: agentField.value, value: agentField.value };
        setSelectedAgentNo(agentField.value);
        setCurrentOption(fallback);

        getAgentScreen({ params: { keyWord: agentField.value } }).then((res) => {
            const opts =
                res.data?.map((item) => ({
                    label: item.agentName!,
                    value: item.agentNo!,
                })) || [];
            const matched = opts.find(
                (o) => o.label === agentField.value || o.value === agentField.value
            );
            if (matched) {
                setSelectedAgentNo(matched.value);
                setCurrentOption(matched);
            }
        });
    }, [agentField?.value, isEditable]);

    // 合并选项：currentOption 始终置顶，去除列表中重复项
    const mergedAgentOptions = useMemo(() => {
        if (!currentOption) return agentOptions;
        const filtered = agentOptions.filter((o) => o.value !== currentOption.value);
        return [currentOption, ...filtered];
    }, [agentOptions, currentOption]);

    const handleChange = (value: string) => {
        setSelectedAgentNo(value);
        // 从列表中找匹配项更新 currentOption
        const matched = agentOptions.find((o) => o.value === value);
        if (matched) {
            setCurrentOption(matched);
        }
        putDataByIdAgent({
            segments: { id: callId! },
            params: { agentNo: value },
        }).then(
            () => Udesk.ui.notify.success('保存成功'),
            () => Udesk.ui.notify.error('保存失败')
        );
    };

    // 加载中或无客服字段 → 不渲染
    if (editableLoading || !agentField) return null;

    return (
        <li className="call-text-list-item">
            <span className="call-text-list-item-title" title={agentField.label}>
                {agentField.label}
            </span>
            <span className="call-text-list-item-content editable-agent-content">
                {isEditable ? (
                    <Select
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label as string)
                                ?.toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={mergedAgentOptions}
                        onDropdownVisibleChange={(open) => {
                            if (open) loadAllAgents();
                        }}
                        onChange={handleChange}
                        placeholder="请选择客服"
                        loading={loading}
                        value={selectedAgentNo}
                        style={{ width: 140 }}
                        size="small"
                    />
                ) : (
                    <span
                        className="call-text-list-item-content-with-copy"
                        title={agentField.value}
                    >
                        {agentField.value || '-'}
                    </span>
                )}
            </span>
        </li>
    );
});

export default Template;
